import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import SEO from "../components/SEO";
import { hasVerifiedDailyMedImage } from "../lib/homepageProduct";
import { trackEvent } from "../lib/analytics";
import { HOME_META_DESCRIPTION } from "@shared/publicPageSeoBuilders";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { useStorefrontPrices } from "../hooks/useStorefrontPrices";
import { GuestPrice } from "../components/GuestPrice";
import { useSSRData } from "../contexts/SSRDataContext";
import EditorialArticleCards from "../components/insights/EditorialArticleCards";
import type { EditorialArticleCard } from "../types/insights";

export function BestSellerImage({ product, onImageError }: { product: any; onImageError: () => void }) {
  if (!hasVerifiedDailyMedImage(product)) return null;

  return (
    <img
      src={product.imageUrl}
      alt=""
      className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
      onError={onImageError}
    />
  );
}

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

function AnimatedCounter({ target, suffix = "", prefix = "" }: { target: number; suffix?: string; prefix?: string }) {
  const [count, setCount] = useState(0);
  const { ref, isVisible } = useInView(0.5);

  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const duration = 2000;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [isVisible, target]);

  return (
    <span ref={ref}>
      {/* Animated span hidden from AT to suppress noisy count-up announcements */}
      <span aria-hidden="true">{prefix}{count.toLocaleString()}{suffix}</span>
      {/* Static final value for screen readers */}
      <span className="sr-only">{prefix}{target.toLocaleString()}{suffix}</span>
    </span>
  );
}

function RotatingText() {
  const words = ["Transparency", "Trust", "Partnership", "Innovation"];
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => { setIndex(i => (i + 1) % words.length); setFade(true); }, 400);
    }, 3000);
    return () => clearInterval(interval);
  }, [prefersReducedMotion]);

  return (
    <>
      <span className="sr-only">Built on Transparency, Trust, Partnership, and Innovation.</span>
      <span aria-hidden="true" className={`inline-block transition-all duration-400 ${fade ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
        {words[index]}
      </span>
    </>
  );
}

const fallingItems = [
  { left: "3%", dur: "12s", delay: "-2s", rot1: "15deg", rot2: "375deg", sway: "20px", opacity: "0.5", cls: "falling-pill-sway",
    svg: `<svg width="60" height="28" viewBox="0 0 44 20"><defs><linearGradient id="c1a" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#93bbf9" /><stop offset="20%" stop-color="#5B9BF5" /><stop offset="50%" stop-color="#3B82F6" /><stop offset="100%" stop-color="#1E40AF" /></linearGradient><linearGradient id="c1b" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#ffffff" /><stop offset="20%" stop-color="#EFF6FF" /><stop offset="50%" stop-color="#DBEAFE" /><stop offset="100%" stop-color="#BFDBFE" /></linearGradient><linearGradient id="c1s" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fff" stop-opacity="0.45"/><stop offset="30%" stop-color="#fff" stop-opacity="0.1"/><stop offset="70%" stop-color="#000" stop-opacity="0"/><stop offset="100%" stop-color="#000" stop-opacity="0.18"/></linearGradient></defs><path d="M 18,0 L 34,0 A 10,10 0 0,1 34,20 L 18,20 Z" fill="url(#c1b)"/><path d="M 22,0 L 10,0 A 10,10 0 0,0 10,20 L 22,20 Z" fill="url(#c1a)"/><rect x="20" y="0" width="1.5" height="20" rx="0.75" fill="#1E3A8A" opacity="0.15"/><rect x="0" y="0" width="44" height="20" rx="10" fill="url(#c1s)"/><ellipse cx="12" cy="4" rx="7" ry="2" fill="#fff" opacity="0.35" transform="rotate(-6 12 4)"/><ellipse cx="33" cy="4.5" rx="5" ry="1.5" fill="#fff" opacity="0.2" transform="rotate(-6 33 4.5)"/></svg>` },

  { left: "10%", dur: "16s", delay: "-8s", rot1: "-10deg", rot2: "350deg", sway: "14px", opacity: "0.45", cls: "falling-pill-drift",
    svg: `<svg width="28" height="28" viewBox="0 0 24 24"><defs><radialGradient id="t1" cx="38%" cy="32%" r="55%"><stop offset="0%" stop-color="#E8E0FF" /><stop offset="45%" stop-color="#A5B4FC" /><stop offset="100%" stop-color="#6366F1" /></radialGradient><linearGradient id="t1s" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fff" stop-opacity="0.3"/><stop offset="55%" stop-color="#fff" stop-opacity="0"/><stop offset="100%" stop-color="#000" stop-opacity="0.1"/></linearGradient></defs><circle cx="12" cy="12" r="10.5" fill="url(#t1)"/><circle cx="12" cy="12" r="10.5" fill="url(#t1s)"/><line x1="3.5" y1="12" x2="20.5" y2="12" stroke="#4338CA" stroke-width="0.6" opacity="0.25" stroke-linecap="round"/><ellipse cx="9.5" cy="7" rx="5.5" ry="3.2" fill="#fff" opacity="0.28" transform="rotate(-15 9.5 7)"/></svg>` },

  { left: "18%", dur: "14s", delay: "-1s", rot1: "50deg", rot2: "410deg", sway: "22px", opacity: "0.45", cls: "falling-pill-sway",
    svg: `<svg width="55" height="25" viewBox="0 0 40 18"><defs><linearGradient id="c2a" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#b8a8ff" /><stop offset="20%" stop-color="#9381FF" /><stop offset="50%" stop-color="#7C3AED" /><stop offset="100%" stop-color="#4C1D95" /></linearGradient><linearGradient id="c2b" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#ffffff" /><stop offset="20%" stop-color="#F5F3FF" /><stop offset="50%" stop-color="#EDE9FE" /><stop offset="100%" stop-color="#DDD6FE" /></linearGradient><linearGradient id="c2s" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fff" stop-opacity="0.42"/><stop offset="30%" stop-color="#fff" stop-opacity="0.08"/><stop offset="70%" stop-color="#000" stop-opacity="0"/><stop offset="100%" stop-color="#000" stop-opacity="0.18"/></linearGradient></defs><path d="M 16,0 L 31,0 A 9,9 0 0,1 31,18 L 16,18 Z" fill="url(#c2b)"/><path d="M 20,0 L 9,0 A 9,9 0 0,0 9,18 L 20,18 Z" fill="url(#c2a)"/><rect x="18" y="0" width="1.4" height="18" rx="0.7" fill="#4C1D95" opacity="0.15"/><rect x="0" y="0" width="40" height="18" rx="9" fill="url(#c2s)"/><ellipse cx="10" cy="4" rx="6" ry="1.8" fill="#fff" opacity="0.35" transform="rotate(-8 10 4)"/><ellipse cx="30" cy="4.5" rx="5" ry="1.5" fill="#fff" opacity="0.18" transform="rotate(-8 30 4.5)"/></svg>` },

  { left: "26%", dur: "18s", delay: "-12s", rot1: "5deg", rot2: "365deg", sway: "10px", opacity: "0.35", cls: "falling-pill-blur",
    svg: `<svg width="26" height="26" viewBox="0 0 22 22"><defs><radialGradient id="t2" cx="36%" cy="30%" r="58%"><stop offset="0%" stop-color="#C9DEFF" /><stop offset="50%" stop-color="#60A5FA" /><stop offset="100%" stop-color="#1D4ED8" /></radialGradient><linearGradient id="t2s" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fff" stop-opacity="0.3"/><stop offset="55%" stop-color="#fff" stop-opacity="0"/><stop offset="100%" stop-color="#000" stop-opacity="0.1"/></linearGradient></defs><circle cx="11" cy="11" r="9.5" fill="url(#t2)"/><circle cx="11" cy="11" r="9.5" fill="url(#t2s)"/><line x1="4" y1="11" x2="18" y2="11" stroke="#1E3A8A" stroke-width="0.5" opacity="0.2" stroke-linecap="round"/><line x1="11" y1="4" x2="11" y2="18" stroke="#1E3A8A" stroke-width="0.4" opacity="0.12" stroke-linecap="round"/><ellipse cx="8.5" cy="7" rx="4.5" ry="2.8" fill="#fff" opacity="0.25" transform="rotate(-18 8.5 7)"/></svg>` },

  { left: "35%", dur: "11s", delay: "-5s", rot1: "70deg", rot2: "430deg", sway: "16px", opacity: "0.45", cls: "falling-pill-pulse",
    svg: `<svg width="63" height="25" viewBox="0 0 46 18"><defs><linearGradient id="c3a" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#a8d2fa" /><stop offset="20%" stop-color="#7DBBF7" /><stop offset="50%" stop-color="#2563EB" /><stop offset="100%" stop-color="#1E3A8A" /></linearGradient><linearGradient id="c3b" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#ffffff" /><stop offset="20%" stop-color="#FFFBEB" /><stop offset="50%" stop-color="#FDE68A" /><stop offset="100%" stop-color="#F59E0B" /></linearGradient><linearGradient id="c3s" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fff" stop-opacity="0.45"/><stop offset="30%" stop-color="#fff" stop-opacity="0.1"/><stop offset="70%" stop-color="#000" stop-opacity="0"/><stop offset="100%" stop-color="#000" stop-opacity="0.16"/></linearGradient></defs><path d="M 19,0 L 37,0 A 9,9 0 0,1 37,18 L 19,18 Z" fill="url(#c3b)"/><path d="M 23,0 L 9,0 A 9,9 0 0,0 9,18 L 23,18 Z" fill="url(#c3a)"/><rect x="21" y="0" width="1.4" height="18" rx="0.7" fill="#1E3A8A" opacity="0.13"/><rect x="0" y="0" width="46" height="18" rx="9" fill="url(#c3s)"/><ellipse cx="12" cy="4.5" rx="7" ry="2" fill="#fff" opacity="0.35" transform="rotate(-5 12 4.5)"/><ellipse cx="35" cy="5" rx="5" ry="1.5" fill="#fff" opacity="0.2" transform="rotate(-5 35 5)"/></svg>` },

  { left: "43%", dur: "15s", delay: "-10s", rot1: "20deg", rot2: "380deg", sway: "25px", opacity: "0.4", cls: "falling-pill-sway",
    svg: `<svg width="48" height="22" viewBox="0 0 34 16"><defs><linearGradient id="c4a" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#f9a0cb" /><stop offset="20%" stop-color="#F472B6" /><stop offset="50%" stop-color="#DB2777" /><stop offset="100%" stop-color="#9D174D" /></linearGradient><linearGradient id="c4b" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#ffffff" /><stop offset="20%" stop-color="#FFF1F2" /><stop offset="50%" stop-color="#FECDD3" /><stop offset="100%" stop-color="#FDA4AF" /></linearGradient><linearGradient id="c4s" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fff" stop-opacity="0.43"/><stop offset="30%" stop-color="#fff" stop-opacity="0.08"/><stop offset="70%" stop-color="#000" stop-opacity="0"/><stop offset="100%" stop-color="#000" stop-opacity="0.18"/></linearGradient></defs><path d="M 13,0 L 26,0 A 8,8 0 0,1 26,16 L 13,16 Z" fill="url(#c4b)"/><path d="M 17,0 L 8,0 A 8,8 0 0,0 8,16 L 17,16 Z" fill="url(#c4a)"/><rect x="15" y="0" width="1.2" height="16" rx="0.6" fill="#9D174D" opacity="0.15"/><rect x="0" y="0" width="34" height="16" rx="8" fill="url(#c4s)"/><ellipse cx="9" cy="3.5" rx="5" ry="1.6" fill="#fff" opacity="0.35" transform="rotate(-8 9 3.5)"/><ellipse cx="25" cy="4" rx="4" ry="1.3" fill="#fff" opacity="0.18" transform="rotate(-8 25 4)"/></svg>` },

  { left: "52%", dur: "13s", delay: "-7s", rot1: "40deg", rot2: "400deg", sway: "18px", opacity: "0.45", cls: "falling-pill-drift",
    svg: `<svg width="26" height="26" viewBox="0 0 22 22"><defs><radialGradient id="t3" cx="35%" cy="28%" r="58%"><stop offset="0%" stop-color="#D1FAE5" /><stop offset="45%" stop-color="#6EE7B7" /><stop offset="100%" stop-color="#047857" /></radialGradient><linearGradient id="t3s" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fff" stop-opacity="0.3"/><stop offset="55%" stop-color="#fff" stop-opacity="0"/><stop offset="100%" stop-color="#000" stop-opacity="0.1"/></linearGradient></defs><circle cx="11" cy="11" r="9.5" fill="url(#t3)"/><circle cx="11" cy="11" r="9.5" fill="url(#t3s)"/><line x1="3.5" y1="11" x2="18.5" y2="11" stroke="#064E3B" stroke-width="0.5" opacity="0.2" stroke-linecap="round"/><ellipse cx="8.5" cy="6.5" rx="5" ry="3" fill="#fff" opacity="0.28" transform="rotate(-12 8.5 6.5)"/></svg>` },

  { left: "62%", dur: "17s", delay: "-14s", rot1: "-25deg", rot2: "335deg", sway: "12px", opacity: "0.35", cls: "falling-pill-blur",
    svg: `<svg width="58" height="25" viewBox="0 0 42 18"><defs><linearGradient id="c5a" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#ddd0fe" /><stop offset="20%" stop-color="#C4B5FD" /><stop offset="50%" stop-color="#8B5CF6" /><stop offset="100%" stop-color="#5B21B6" /></linearGradient><linearGradient id="c5b" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#ffffff" /><stop offset="20%" stop-color="#FAF5FF" /><stop offset="50%" stop-color="#F3E8FF" /><stop offset="100%" stop-color="#E9D5FF" /></linearGradient><linearGradient id="c5s" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fff" stop-opacity="0.45"/><stop offset="30%" stop-color="#fff" stop-opacity="0.1"/><stop offset="70%" stop-color="#000" stop-opacity="0"/><stop offset="100%" stop-color="#000" stop-opacity="0.18"/></linearGradient></defs><path d="M 17,0 L 33,0 A 9,9 0 0,1 33,18 L 17,18 Z" fill="url(#c5b)"/><path d="M 21,0 L 9,0 A 9,9 0 0,0 9,18 L 21,18 Z" fill="url(#c5a)"/><rect x="19" y="0" width="1.4" height="18" rx="0.7" fill="#5B21B6" opacity="0.14"/><rect x="0" y="0" width="42" height="18" rx="9" fill="url(#c5s)"/><ellipse cx="11" cy="4.5" rx="6.5" ry="1.8" fill="#fff" opacity="0.35" transform="rotate(-6 11 4.5)"/><ellipse cx="32" cy="5" rx="4.5" ry="1.4" fill="#fff" opacity="0.18" transform="rotate(-6 32 5)"/></svg>` },

  { left: "70%", dur: "10s", delay: "-4s", rot1: "35deg", rot2: "395deg", sway: "20px", opacity: "0.5", cls: "falling-pill-sway",
    svg: `<svg width="52" height="22" viewBox="0 0 38 16"><defs><linearGradient id="c6a" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#8ef3e3" /><stop offset="20%" stop-color="#5EEAD4" /><stop offset="50%" stop-color="#0D9488" /><stop offset="100%" stop-color="#115E59" /></linearGradient><linearGradient id="c6b" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#ffffff" /><stop offset="20%" stop-color="#F0FDFA" /><stop offset="50%" stop-color="#CCFBF1" /><stop offset="100%" stop-color="#99F6E4" /></linearGradient><linearGradient id="c6s" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fff" stop-opacity="0.43"/><stop offset="30%" stop-color="#fff" stop-opacity="0.08"/><stop offset="70%" stop-color="#000" stop-opacity="0"/><stop offset="100%" stop-color="#000" stop-opacity="0.18"/></linearGradient></defs><path d="M 15,0 L 30,0 A 8,8 0 0,1 30,16 L 15,16 Z" fill="url(#c6b)"/><path d="M 19,0 L 8,0 A 8,8 0 0,0 8,16 L 19,16 Z" fill="url(#c6a)"/><rect x="17" y="0" width="1.2" height="16" rx="0.6" fill="#115E59" opacity="0.15"/><rect x="0" y="0" width="38" height="16" rx="8" fill="url(#c6s)"/><ellipse cx="10" cy="3.5" rx="6" ry="1.6" fill="#fff" opacity="0.35" transform="rotate(-10 10 3.5)"/><ellipse cx="29" cy="4" rx="4.5" ry="1.4" fill="#fff" opacity="0.18" transform="rotate(-10 29 4)"/></svg>` },

  { left: "78%", dur: "14s", delay: "-9s", rot1: "60deg", rot2: "420deg", sway: "15px", opacity: "0.4", cls: "falling-pill-drift",
    svg: `<svg width="50" height="25" viewBox="0 0 36 18"><defs><linearGradient id="o1a" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fef0b8" /><stop offset="20%" stop-color="#FDE68A" /><stop offset="50%" stop-color="#F59E0B" /><stop offset="100%" stop-color="#B45309" /></linearGradient><linearGradient id="o1s" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fff" stop-opacity="0.45"/><stop offset="30%" stop-color="#fff" stop-opacity="0.1"/><stop offset="70%" stop-color="#000" stop-opacity="0"/><stop offset="100%" stop-color="#000" stop-opacity="0.15"/></linearGradient></defs><rect x="0" y="0" width="36" height="18" rx="9" fill="url(#o1a)"/><rect x="0" y="0" width="36" height="18" rx="9" fill="url(#o1s)"/><line x1="7" y1="9" x2="29" y2="9" stroke="#78350F" stroke-width="0.5" opacity="0.18" stroke-linecap="round"/><ellipse cx="15" cy="4.5" rx="8" ry="2.2" fill="#fff" opacity="0.3" transform="rotate(-5 15 4.5)"/></svg>` },

  { left: "86%", dur: "11s", delay: "-6s", rot1: "-15deg", rot2: "345deg", sway: "22px", opacity: "0.45", cls: "falling-pill-pulse",
    svg: `<svg width="44" height="19" viewBox="0 0 32 14"><defs><linearGradient id="c7a" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#93c5fd" /><stop offset="20%" stop-color="#60A5FA" /><stop offset="50%" stop-color="#2563EB" /><stop offset="100%" stop-color="#1E3A8A" /></linearGradient><linearGradient id="c7b" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#ffffff" /><stop offset="20%" stop-color="#EFF6FF" /><stop offset="50%" stop-color="#DBEAFE" /><stop offset="100%" stop-color="#BFDBFE" /></linearGradient><linearGradient id="c7s" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fff" stop-opacity="0.43"/><stop offset="30%" stop-color="#fff" stop-opacity="0.08"/><stop offset="70%" stop-color="#000" stop-opacity="0"/><stop offset="100%" stop-color="#000" stop-opacity="0.18"/></linearGradient></defs><path d="M 12,0 L 25,0 A 7,7 0 0,1 25,14 L 12,14 Z" fill="url(#c7b)"/><path d="M 16,0 L 7,0 A 7,7 0 0,0 7,14 L 16,14 Z" fill="url(#c7a)"/><rect x="14" y="0" width="1.1" height="14" rx="0.55" fill="#1E3A8A" opacity="0.15"/><rect x="0" y="0" width="32" height="14" rx="7" fill="url(#c7s)"/><ellipse cx="8" cy="3.5" rx="5" ry="1.5" fill="#fff" opacity="0.35" transform="rotate(-8 8 3.5)"/><ellipse cx="23" cy="3.8" rx="4" ry="1.3" fill="#fff" opacity="0.18" transform="rotate(-8 23 3.8)"/></svg>` },

  { left: "94%", dur: "16s", delay: "-11s", rot1: "45deg", rot2: "405deg", sway: "18px", opacity: "0.35", cls: "falling-pill-sway",
    svg: `<svg width="50" height="22" viewBox="0 0 36 16"><defs><linearGradient id="c8a" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#c4a8fc" /><stop offset="20%" stop-color="#A78BFA" /><stop offset="50%" stop-color="#7C3AED" /><stop offset="100%" stop-color="#4C1D95" /></linearGradient><linearGradient id="c8b" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#ffffff" /><stop offset="20%" stop-color="#FAF5FF" /><stop offset="50%" stop-color="#EDE9FE" /><stop offset="100%" stop-color="#DDD6FE" /></linearGradient><linearGradient id="c8s" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fff" stop-opacity="0.43"/><stop offset="30%" stop-color="#fff" stop-opacity="0.08"/><stop offset="70%" stop-color="#000" stop-opacity="0"/><stop offset="100%" stop-color="#000" stop-opacity="0.18"/></linearGradient></defs><path d="M 14,0 L 28,0 A 8,8 0 0,1 28,16 L 14,16 Z" fill="url(#c8b)"/><path d="M 18,0 L 8,0 A 8,8 0 0,0 8,16 L 18,16 Z" fill="url(#c8a)"/><rect x="16" y="0" width="1.2" height="16" rx="0.6" fill="#4C1D95" opacity="0.15"/><rect x="0" y="0" width="36" height="16" rx="8" fill="url(#c8s)"/><ellipse cx="9" cy="3.5" rx="5.5" ry="1.5" fill="#fff" opacity="0.35" transform="rotate(-10 9 3.5)"/><ellipse cx="27" cy="4" rx="4.5" ry="1.3" fill="#fff" opacity="0.18" transform="rotate(-10 27 4)"/></svg>` },
];

function DailyDealTeaser() {
  const [deal, setDeal] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [, setTick] = useState(0);

  useEffect(() => {
    fetch("/api/auctions?status=active")
      .then(r => {
        if (!r.ok) throw new Error("Failed to fetch auctions");
        return r.json();
      })
      .then(data => {
        const active = Array.isArray(data) ? data : (data.auctions || []);
        if (active.length > 0) setDeal(active[0]);
        setLoading(false);
      })
      .catch((err) => { console.error("Failed to fetch daily deal:", err); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!deal) return;
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, [deal]);

  if (loading) {
    return (
      <section className="py-4 bg-gradient-to-r from-accent via-blue-600 to-indigo-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex w-10 h-10 rounded-lg bg-white/20 items-center justify-center flex-shrink-0 animate-pulse" />
              <div className="space-y-1.5">
                <div className="h-4 w-24 bg-white/30 rounded animate-pulse" />
                <div className="h-3 w-40 bg-white/20 rounded animate-pulse" />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right space-y-1.5">
                <div className="h-3 w-16 bg-white/20 rounded animate-pulse" />
                <div className="h-5 w-14 bg-white/30 rounded animate-pulse" />
              </div>
              <div className="text-right space-y-1.5">
                <div className="h-3 w-12 bg-white/20 rounded animate-pulse" />
                <div className="h-5 w-20 bg-white/30 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!deal) return null;

  const endTime = new Date(deal.endTime);
  const endMs = endTime.getTime();
  const isValidDate = !isNaN(endMs);
  const msLeft = isValidDate ? endMs - Date.now() : Number.MAX_SAFE_INTEGER;

  // Only hide the teaser when we have a valid date that has already expired
  if (isValidDate && msLeft <= 0) return null;

  const hours = isValidDate ? Math.floor(msLeft / (1000 * 60 * 60)) : 0;
  const minutes = isValidDate ? Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60)) : 0;
  const seconds = isValidDate ? Math.floor((msLeft % (1000 * 60)) / 1000) : 0;
  const isUrgent = isValidDate && msLeft < 1 * 60 * 60 * 1000;
  const isSoon = isValidDate && msLeft < 4 * 60 * 60 * 1000;
  const currentBid = deal.currentHighBid || deal.startingPrice;
  const formattedBid = `$${currentBid}`;

  const countdownDisplay = !isValidDate
    ? "Ending soon"
    : hours > 0
    ? `${hours}h ${minutes}m`
    : `${minutes}m ${seconds}s`;

  return (
    <section className="py-4 bg-gradient-to-r from-accent via-blue-600 to-indigo-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to={`/auctions/${deal.id}`} className="flex items-center justify-between gap-4 flex-wrap group">
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex w-10 h-10 rounded-lg bg-white/20 items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-white font-bold text-sm sm:text-base">Daily Deal</span>
                <span className="bg-white/20 text-white text-xs font-medium px-2 py-0.5 rounded-full">{deal.bidCount} bid{deal.bidCount !== 1 ? "s" : ""}</span>
              </div>
              <div className="text-white/80 text-xs sm:text-sm truncate max-w-[200px] sm:max-w-none">{deal.productName || deal.title}</div>
            </div>
          </div>
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="text-right">
              <div className="text-white/70 text-xs">Current Bid</div>
              <div className="text-white font-bold text-lg">{formattedBid}</div>
            </div>
            <div className="text-right">
              <div className="text-white/70 text-xs">Ends In</div>
              <div className={`font-bold text-lg font-mono ${isUrgent ? "text-red-300 animate-pulse" : isSoon ? "text-yellow-300" : "text-white"}`}>
                {countdownDisplay}
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-1 text-white/80 text-sm font-medium group-hover:gap-2 transition-all">
              Bid Now
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  const { user, pricingScope } = useAuth();
  const { seoData } = useSSRData();
  const latest = useQuery<{ articles: EditorialArticleCard[] }>({
    queryKey: ["editorial", "latest"],
    initialData: seoData?.type === "home" ? { articles: seoData.latestArticles || [] } : undefined,
    staleTime: 60_000,
    queryFn: async () => {
      const response = await fetch("/api/editorial/latest-articles");
      if (!response.ok) throw new Error("Latest Insights could not be loaded");
      return response.json();
    },
  });
  const latestArticles = latest.data?.articles.slice(0, 3) || [];
  const customerPrices = useStorefrontPrices();
  const [failedImages, setFailedImages] = useState<number[]>([]);
  const { data: movers = [], isLoading: bestSellersLoading, isError: bestSellersError } = useQuery<any[]>({
    queryKey: ["top-movers", pricingScope],
    queryFn: async ({ signal }) => {
      const res = await fetch("/api/products/top-movers", { signal, cache: "no-store" });
      if (!res.ok) throw new Error("Failed to load best sellers");
      return res.json();
    },
  });
  const bestSellers = movers.filter(hasVerifiedDailyMedImage).filter(p => !failedImages.includes(p.id)).slice(0, 4);
  const [topManufacturers, setTopManufacturers] = useState<{ name: string; slug: string; productCount: number }[]>([]);

  useEffect(() => {
    fetch("/api/seo/manufacturers")
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (Array.isArray(data)) setTopManufacturers(data.slice(0, 12)); })
      .catch(() => {});
  }, []);

  const section1 = useInView();
  const section2 = useInView();
  const section3 = useInView();
  const section4 = useInView();
  const section5 = useInView();

  return (
    <>
      <SEO
        title="Wholesale Pharmaceuticals for Pharmacies"
        description={HOME_META_DESCRIPTION}
        path="/"
      />

      <div className="pill-rain-container" aria-hidden="true">
        {fallingItems.map((item, i) => (
          <div key={i} className={item.cls} style={{ left: item.left, "--pill-duration": item.dur, "--pill-delay": item.delay, "--pill-start-rot": item.rot1, "--pill-end-rot": item.rot2, "--pill-sway": item.sway, "--pill-max-opacity": item.opacity } as React.CSSProperties} dangerouslySetInnerHTML={{ __html: item.svg }} />
        ))}
      </div>

      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-[#0a1e4a] to-[#0d2d6b]"></div>
        <img src="/hero-bg-pattern.webp" alt="" aria-hidden="true" width={1536} height={1024} fetchPriority="high" decoding="async" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/50 to-transparent"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="hero-fade-in">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] tracking-tight">
                Wholesale Generic Pharmaceuticals for Licensed Healthcare Providers
              </h1>
              <p className="mt-3 text-lg text-accent-300 font-semibold">
                <RotatingText /> You Can Count On.
              </p>

              <p className="mt-6 text-lg lg:text-xl text-white/70 max-w-lg leading-relaxed">
                 Create a free account, submit your licensing information for review, and see wholesale pricing after verification. No credit card, contract, or purchase is required.
              </p>

              <div className="mt-5 inline-flex items-center gap-2 bg-green-500/15 border border-green-400/25 rounded-full px-4 py-2">
                <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                 <span className="text-sm text-green-300 font-medium">Credential review typically takes 1–2 business days</span>
              </div>

              <div className="mt-6 flex flex-wrap gap-4">
                <Link to="/register" className="bg-accent hover:bg-accent-600 text-white rounded-btn px-8 py-4 font-semibold text-lg transition-all duration-200 hover:shadow-lg hover:shadow-accent/25 hover:-translate-y-0.5">
                   Sign up to purchase drugs
                </Link>
              </div>
            </div>

            <div className="hidden lg:flex justify-center relative">
              <div className="hero-illustration">
                <picture>
                  <source srcSet="/hero-pharmacy.webp" type="image/webp" />
                  <img src="/hero-pharmacy.png" alt="Pharmacist accessing wholesale pharmaceutical inventory through Premier Pharma's online ordering platform" width="2000" height="2000" className="w-full max-w-md drop-shadow-2xl" loading="eager" fetchPriority="high" />
                </picture>
              </div>
            </div>
          </div>

          <div className="mt-16 lg:mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8 border-t border-white/10 pt-10 pb-6">
            {[
              { value: 1, suffix: "", label: "Free account to start" },
              { value: 1, suffix: "–2", label: "Business days for typical review" },
              { value: 0, suffix: "", label: "Credit card required" },
              { value: 0, suffix: "", label: "Purchase required" },
            ].map((stat, i) => (
              <div key={i} className="text-center hero-fade-in" style={{ animationDelay: `${0.3 + i * 0.1}s` }}>
                <div className="text-3xl lg:text-4xl font-bold text-white">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-sm text-white/70 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-8 sm:gap-12 flex-wrap">
            <a href="https://mybadges.us.openbadges.me/api/badgeitem/c44016ac-de61-418c-951f-86c0cddd258b/metadata" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="/nabp-badge.png" alt="NABP Accredited Drug Distributor" className="w-8 h-8 object-contain" />
              <span className="text-sm font-semibold text-primary">NABP Accredited</span>
            </a>
            <a href="https://www.bbb.org/us/id/boise/profile/pharmaceutical-supplies/premier-pharma-1296-1000131516" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="https://seal-alaskaoregonwesternwashington.bbb.org/logo/rbhzbul/bbb-1000131516.png" alt="BBB Accredited Business" className="h-8 object-contain" />
              <span className="text-sm font-semibold text-primary">BBB Accredited</span>
            </a>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 bg-purple-500 rounded-full"></span>
              <span className="text-sm font-semibold text-primary">DSCSA Resources</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 bg-amber-500 rounded-full"></span>
              <span className="text-sm font-semibold text-primary">DEA Licensed</span>
            </div>
          </div>
        </div>
      </section>

      <DailyDealTeaser />

      {(bestSellersLoading || bestSellers.length > 0 || bestSellersError) && (
        <section ref={section3.ref} className={`py-24 bg-white transition-all duration-700 ${section3.isVisible ? "opacity-100 translate-y-0" : "opacity-90 translate-y-4"}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <span className="inline-block bg-accent/10 text-accent text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Popular Products</span>
                <h2 className="text-3xl lg:text-4xl font-bold text-primary">Best Sellers</h2>
              </div>
              <Link to="/products" aria-label="View all products" className="hidden sm:inline-flex items-center gap-2 text-accent font-semibold hover:gap-3 transition-all duration-200">
                View All
                <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </Link>
            </div>
            {bestSellersError ? (
              <div className="text-center py-12 text-gray-500">
                <p className="mb-2">Unable to load products right now.</p>
                <Link to="/products" className="text-accent font-medium hover:underline">Browse all products</Link>
              </div>
            ) : bestSellersLoading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[0, 1, 2, 3].map(i => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="bg-gray-100 h-40 animate-pulse" />
                    <div className="p-5 space-y-3">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                      <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" />
                      <div className="flex items-center justify-between">
                        <div className="h-4 bg-gray-200 rounded animate-pulse w-16" />
                        <div className="h-5 bg-gray-100 rounded-full animate-pulse w-20" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {bestSellers.map((p: any) => (
                <div key={p.id} className="group bg-white rounded-2xl border border-gray-100 hover:border-accent/20 hover:shadow-xl transition-all duration-300 overflow-hidden">
                <Link to={`/products/${p.id}`} className="block">
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-8 flex items-center justify-center h-40">
                    <BestSellerImage
                      product={p}
                      onImageError={() => setFailedImages(current => [...current, p.id])}
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-primary text-sm leading-tight group-hover:text-accent transition-colors line-clamp-2">{p.name}</h3>
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                      <span className="font-mono">{p.ndc}</span>
                      <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                      <span>{p.strength}</span>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      {user && <span className="text-accent font-bold">{user.accountStatus === "active" && p.price ? `$${customerPrices[p.id] ?? p.price}` : <span className="text-xs font-medium">Pricing available after account approval</span>}</span>}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${p.inStock ? "bg-green-50 text-green-600" : "bg-blue-50 text-blue-600"}`}>
                        {p.inStock ? "In Stock" : "2-Day Delivery"}
                      </span>
                    </div>
                  </div>
                </Link>
                {!user && <div className="px-5 pb-5"><GuestPrice estimate={p.estimatedGuestPrice} /></div>}
                </div>
              ))}
            </div>
            <div className="text-center mt-8 sm:hidden">
              <Link to="/products" className="btn-primary">View All Products</Link>
            </div>
            </>
            )}
          </div>
        </section>
      )}

      <section className="relative py-20 overflow-hidden bg-gradient-to-b from-primary via-[#0a1e4a] to-primary">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M20 0v40M0 20h40' stroke='%23ffffff' stroke-width='0.5' fill='none'/%3E%3C/g%3E%3C/svg%3E\")" }}></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-green-500/20 border border-green-400/30 text-green-300 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse motion-reduce:animate-none"></span>
                Included Free
              </span>
              <h2 className="text-3xl lg:text-5xl font-bold text-white leading-tight">
                Free DSCSA Software<br />
                <span className="text-accent-300">with Every Account</span>
              </h2>
              <p className="mt-6 text-white/75 text-lg leading-relaxed max-w-lg">
                Every eligible business can start with an account and free DSCSA compliance software, with no purchase required. After credential review, wholesale ordering and separate paid ScriptTrack products are optional choices.
              </p>
              <div className="mt-8 flex items-center gap-4 bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-accent/20 flex items-center justify-center">
                  <span className="text-2xl font-bold text-accent-300">$0</span>
                </div>
                <div>
                  <div className="text-white font-semibold">No extra charge</div>
                  <div className="text-white/70 text-sm">Free DSCSA compliance software. No purchase required.</div>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/software" className="bg-accent hover:bg-accent-600 text-white rounded-btn px-8 py-4 font-semibold text-lg transition-all duration-200 hover:shadow-lg hover:shadow-accent/25 hover:-translate-y-0.5">
                  See the free account offer
                </Link>
                <Link to="/resources/dscsa-compliance" className="border-2 border-white/30 text-white rounded-btn px-8 py-4 font-semibold text-lg hover:bg-white/10 transition-all duration-200 hover:-translate-y-0.5">
                  DSCSA Compliance Guide
                </Link>
              </div>
            </div>

            <div className="flex flex-col items-center gap-6">
              <picture>
                <source srcSet="/pharma-illustration.webp" type="image/webp" />
                <img src="/pharma-illustration.png" alt="Pharmaceutical compliance illustration" width="2000" height="2000" className="w-full max-w-sm drop-shadow-2xl" loading="lazy" />
              </picture>
              <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
                {[
                  { title: "Transaction Tracking", icon: <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none"><path d="M4 6h12M4 10h12M4 14h6" stroke="#93B4F8" strokeWidth="1.5" strokeLinecap="round"/></svg> },
                  { title: "Serialization", icon: <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none"><rect x="3" y="5" width="2" height="10" rx="0.5" fill="#6EE7B7"/><rect x="6" y="5" width="3" height="10" rx="0.5" fill="#6EE7B7"/><rect x="10.5" y="5" width="1.5" height="10" rx="0.5" fill="#6EE7B7"/><rect x="13.5" y="5" width="3" height="10" rx="0.5" fill="#6EE7B7"/></svg> },
                  { title: "Auto Reporting", icon: <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none"><rect x="4" y="3" width="12" height="14" rx="2" stroke="#C4B5FD" strokeWidth="1.5"/><path d="M7 8h6M7 11h4" stroke="#C4B5FD" strokeWidth="1" strokeLinecap="round"/></svg> },
                  { title: "Real-Time Alerts", icon: <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none"><path d="M10 4v5M10 13v1" stroke="#FCD34D" strokeWidth="1.5" strokeLinecap="round"/><path d="M10 3l7 12H3L10 3z" stroke="#FCD34D" strokeWidth="1.5" strokeLinejoin="round" fill="none"/></svg> },
                ].map((f, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 flex items-center gap-2.5">
                    {f.icon}
                    <span className="text-white/80 text-xs font-medium">{f.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-4 sm:gap-6 flex-wrap">
            <span className="text-xs text-gray-600 uppercase tracking-wider font-medium">As Seen In</span>
            <a href="https://www.ibtimes.com/jacob-fuchs-software-engineerpharmacist-shaping-connected-healthcare-vision-3795303" onClick={() => trackEvent("press_coverage_clicked", { publication: "international_business_times" })} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg px-5 py-2.5 transition-colors group">
              <span className="font-bold text-primary text-sm tracking-wide">INTERNATIONAL BUSINESS TIMES</span>
              <svg className="w-4 h-4 text-gray-400 group-hover:text-accent transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
            </a>
            <a href="https://www.medicaldaily.com/incentive-problem-healthcare-jacob-fuchss-journey-fix-it-474505" onClick={() => trackEvent("press_coverage_clicked", { publication: "medical_daily" })} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg px-5 py-2.5 transition-colors group">
              <span className="font-bold text-primary text-sm tracking-wide">MEDICAL DAILY</span>
              <svg className="w-4 h-4 text-gray-400 group-hover:text-accent transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
            </a>
            <a href="https://mybadges.us.openbadges.me/api/badgeitem/c44016ac-de61-418c-951f-86c0cddd258b/metadata" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg px-3 py-1.5 transition-colors group">
              <img src="/nabp-badge.png" alt="NABP Accredited Drug Distributor" className="w-6 h-6 object-contain" />
              <span className="text-xs font-medium text-green-700">NABP Drug Distributor</span>
            </a>
            <span className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-full px-3 py-1">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              <span className="text-xs font-medium text-blue-700">BBB Accredited</span>
            </span>
          </div>
        </div>
      </section>

      <section ref={section1.ref} className={`py-24 bg-white transition-all duration-700 ${section1.isVisible ? "opacity-100 translate-y-0" : "opacity-90 translate-y-4"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block bg-accent/10 text-accent text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">Why Premier Pharma</span>
              <h2 className="text-3xl lg:text-5xl font-bold text-primary leading-tight">
                Built on integrity.<br />Driven by partnership.
              </h2>
              <p className="mt-6 text-gray-600 text-lg leading-relaxed max-w-lg">
                We don't just sell pharmaceuticals — we build lasting relationships. Our team works as an extension of yours, delivering competitive pricing with the compliance infrastructure your pharmacy demands.
              </p>
              <div className="mt-8">
                <Link to="/about" className="inline-flex items-center gap-2 text-accent font-semibold hover:gap-3 transition-all duration-200">
                  Learn Our Story
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </Link>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              {[
                {
                  title: "Competitive Pricing",
                  desc: "Transparent wholesale pricing that keeps your margins healthy. No hidden fees, no surprises.",
                  icon: (
                    <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
                      <rect width="32" height="32" rx="8" fill="#054FF0" fillOpacity="0.1"/>
                      <path d="M16 8v16M12 12l4-4 4 4M10 20h12" stroke="#054FF0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ),
                },
                {
                  title: "Compliance-minded workflows",
                  desc: "Start with included DSCSA workflow software and the support of a licensed wholesale distributor.",
                  icon: (
                    <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
                      <rect width="32" height="32" rx="8" fill="#10B981" fillOpacity="0.1"/>
                      <path d="M16 9l-7 4v6c0 4.5 3 8.5 7 10 4-1.5 7-5.5 7-10v-6l-7-4z" stroke="#10B981" strokeWidth="2" strokeLinejoin="round"/>
                      <path d="M12.5 16.5l2.5 2.5 5-5" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ),
                },
                {
                  title: "Fast Fulfillment",
                  desc: "A licensed wholesale team helps your business move from account review to informed ordering.",
                  icon: (
                    <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
                      <rect width="32" height="32" rx="8" fill="#8B5CF6" fillOpacity="0.1"/>
                      <rect x="6" y="12" width="14" height="10" rx="1" stroke="#8B5CF6" strokeWidth="2"/>
                      <path d="M20 15h4l3 4v3h-7" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="13" cy="23" r="2" stroke="#8B5CF6" strokeWidth="2"/>
                      <circle cx="24" cy="23" r="2" stroke="#8B5CF6" strokeWidth="2"/>
                    </svg>
                  ),
                },
                {
                  title: "Dedicated Support",
                  desc: "Real human support is available as you review the account and product options.",
                  icon: (
                    <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
                      <rect width="32" height="32" rx="8" fill="#F59E0B" fillOpacity="0.1"/>
                      <path d="M16 20c3.3 0 6-2.7 6-6s-2.7-6-6-6-6 2.7-6 6 2.7 6 6 6z" stroke="#F59E0B" strokeWidth="2"/>
                      <path d="M8 26c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  ),
                },
              ].map((feature, i) => (
                <div key={i} className="bg-gray-50 hover:bg-white rounded-2xl p-6 border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300 group">
                  <div className="mb-4 group-hover:scale-110 transition-transform duration-300">{feature.icon}</div>
                  <h3 className="text-lg font-bold text-primary mb-2">{feature.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section ref={section2.ref} className={`py-24 bg-gradient-to-b from-gray-50 to-white transition-all duration-700 ${section2.isVisible ? "opacity-100 translate-y-0" : "opacity-90 translate-y-4"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block bg-accent/10 text-accent text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Browse Categories</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-primary">What We Offer</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">Browse wholesale product categories after your eligible business account is verified.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6">
            {[
              {
                name: "Pharmaceuticals", slug: "pharmaceuticals",
                icon: (
                  <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none">
                    <rect width="40" height="40" rx="12" fill="#054FF0" fillOpacity="0.08"/>
                    <rect x="8" y="12" width="12" height="16" rx="6" fill="#054FF0" fillOpacity="0.7"/>
                    <rect x="20" y="12" width="12" height="16" rx="6" fill="#93B4F8" fillOpacity="0.7"/>
                    <rect x="18" y="13" width="4" height="14" rx="1" fill="#fff" fillOpacity="0.5"/>
                  </svg>
                ),
              },
              {
                name: "Animal Health", slug: "animal-health",
                icon: (
                  <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none">
                    <rect width="40" height="40" rx="12" fill="#10B981" fillOpacity="0.08"/>
                    <path d="M15 16c-1-2.5-4-3-4-1s1.5 4 4 4M25 16c1-2.5 4-3 4-1s-1.5 4-4 4M20 28c-4 0-7-3-7-7 0-3 3-6 7-6s7 3 7 6c0 4-3 7-7 7z" stroke="#10B981" strokeWidth="1.5" fill="#10B981" fillOpacity="0.2"/>
                    <circle cx="17" cy="21" r="1.5" fill="#10B981"/><circle cx="23" cy="21" r="1.5" fill="#10B981"/>
                  </svg>
                ),
              },
              {
                name: "Medical/Surgical", slug: "medical-surgical",
                icon: (
                  <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none">
                    <rect width="40" height="40" rx="12" fill="#EF4444" fillOpacity="0.08"/>
                    <rect x="17" y="10" width="6" height="20" rx="1" fill="#EF4444" fillOpacity="0.6"/>
                    <rect x="10" y="17" width="20" height="6" rx="1" fill="#EF4444" fillOpacity="0.6"/>
                  </svg>
                ),
              },
              {
                name: "Caps & Tabs", slug: "caps-and-tabs",
                icon: (
                  <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none">
                    <rect width="40" height="40" rx="12" fill="#8B5CF6" fillOpacity="0.08"/>
                    <circle cx="20" cy="20" r="8" stroke="#8B5CF6" strokeWidth="2" fill="#8B5CF6" fillOpacity="0.15"/>
                    <line x1="14" y1="20" x2="26" y2="20" stroke="#8B5CF6" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                ),
              },
              {
                name: "Diabetic Supplies", slug: "diabetic-supplies",
                icon: (
                  <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none">
                    <rect width="40" height="40" rx="12" fill="#F59E0B" fillOpacity="0.08"/>
                    <path d="M20 10v4M20 26v4M16 14l2 3M22 23l2 3M12 20h4M24 20h4M14 26l3-2M23 16l3-2" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round"/>
                    <circle cx="20" cy="20" r="3" fill="#F59E0B" fillOpacity="0.4" stroke="#F59E0B" strokeWidth="1.5"/>
                  </svg>
                ),
              },
            ].map((cat, i) => (
              <Link key={i} to={`/categories/${cat.slug}`} className="bg-white hover:bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-accent/30 hover:shadow-xl transition-all duration-300 text-center group">
                <div className="flex justify-center mb-4 group-hover:scale-110 transition-transform duration-300">{cat.icon}</div>
                <h3 className="font-semibold text-primary text-sm">{cat.name}</h3>
                <span className="text-accent text-xs mt-2 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  Browse
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {topManufacturers.length > 0 && (
        <section className="py-12 bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <span className="inline-block bg-accent/10 text-accent text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-3">Manufacturers</span>
              <h2 className="text-2xl lg:text-3xl font-bold text-primary">Wholesale from Top Drug Manufacturers</h2>
              <p className="text-gray-500 mt-2 max-w-xl mx-auto text-sm">Browse pharmaceutical products by manufacturer, sourced through licensed channels.</p>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {topManufacturers.map((m) => (
                <Link
                  key={m.slug}
                  to={`/manufacturers/${m.slug}`}
                  className="bg-gray-50 hover:bg-accent/5 border border-gray-200 hover:border-accent/40 rounded-full px-4 py-2 text-sm font-medium text-primary hover:text-accent transition-colors"
                >
                  {m.name}
                  <span className="text-gray-600 ml-2 text-xs">({m.productCount})</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section ref={section4.ref} className={`py-24 bg-gradient-to-b from-white to-background relative overflow-hidden transition-all duration-700 ${section4.isVisible ? "opacity-100 translate-y-0" : "opacity-90 translate-y-4"}`}>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }}></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <span className="inline-block bg-accent/10 text-accent text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Press Coverage</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-primary">In the News</h2>
            <p className="mt-3 text-gray-500 max-w-xl mx-auto">Premier Pharma and founder Jacob Fuchs featured in leading publications.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <a href="https://www.ibtimes.com/jacob-fuchs-software-engineerpharmacist-shaping-connected-healthcare-vision-3795303" onClick={() => trackEvent("press_coverage_clicked", { publication: "international_business_times" })} target="_blank" rel="noopener noreferrer" className="block group">
              <div className="relative bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 h-full">
                <div className="relative h-64 overflow-hidden">
                  <img src="/jacob-fuchs-ibt.jpg" alt="Jacob Fuchs featured in International Business Times" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="inline-flex items-center bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-bold text-primary tracking-wide uppercase">
                      International Business Times
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs text-gray-600">January 4, 2026</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                    <span className="text-xs text-gray-600">Business</span>
                  </div>
                  <h3 className="text-xl font-bold text-primary group-hover:text-accent transition-colors leading-snug mb-3">
                    A Software Engineer-Pharmacist Shaping a Connected Healthcare Vision
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed line-clamp-3">
                    Jacob Fuchs bridges the not commonly intersecting worlds of software engineering and pharmacy, building healthcare infrastructure that is both adaptable and supportive of the people who depend on it.
                  </p>
                  <div className="mt-5 flex items-center gap-2 text-accent font-semibold text-sm group-hover:gap-3 transition-all">
                    Read Article
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                  </div>
                </div>
              </div>
            </a>

            <a href="https://www.medicaldaily.com/incentive-problem-healthcare-jacob-fuchss-journey-fix-it-474505" onClick={() => trackEvent("press_coverage_clicked", { publication: "medical_daily" })} target="_blank" rel="noopener noreferrer" className="block group">
              <div className="relative bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 h-full">
                <div className="relative h-64 overflow-hidden">
                  <img src="/jacob-fuchs-md.jpg" alt="Jacob Fuchs featured in Medical Daily" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="inline-flex items-center bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-bold text-primary tracking-wide uppercase">
                      Medical Daily
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs text-gray-600">January 21, 2026</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                    <span className="text-xs text-gray-600">Healthy Living</span>
                  </div>
                  <h3 className="text-xl font-bold text-primary group-hover:text-accent transition-colors leading-snug mb-3">
                    The Incentive Problem in Healthcare and Jacob Fuchs's Journey to Fix It
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed line-clamp-3">
                    As a pharmacist by training and a builder by instinct, Jacob Fuchs approaches healthcare with a systems-level mindset, creating infrastructure that promotes transparency and strengthens trust.
                  </p>
                  <div className="mt-5 flex items-center gap-2 text-accent font-semibold text-sm group-hover:gap-3 transition-all">
                    Read Article
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                  </div>
                </div>
              </div>
            </a>
          </div>

          <div className="mt-12 bg-white rounded-2xl border border-gray-100 shadow-sm p-8 lg:p-10">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center">
                  <svg className="w-7 h-7 text-accent" fill="currentColor" viewBox="0 0 24 24"><path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/></svg>
                </div>
              </div>
              <div>
                <p className="text-lg lg:text-xl text-gray-700 leading-relaxed italic">
                  "The pharmaceutical industry has an incentive problem. When the people selling you drugs make more money the more you pay, something is fundamentally misaligned. We built Premier Pharma to fix that."
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <div>
                    <div className="font-bold text-primary">Jacob Fuchs</div>
                    <div className="text-sm text-gray-500">Founder, Premier Pharma</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {latestArticles.length > 0 && (
        <section className="bg-[#f3f8f8] py-14 sm:py-16" aria-labelledby="home-latest-insights">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <EditorialArticleCards articles={latestArticles} heading="Latest from Insights" id="home-latest-insights" />
          </div>
        </section>
      )}

      <section ref={section5.ref} className={`relative py-24 overflow-hidden transition-all duration-700 ${section5.isVisible ? "opacity-100 translate-y-0" : "opacity-90 translate-y-4"}`}>
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-[#0a1e4a] to-accent/80"></div>
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "url('/warehouse-interior.webp')", backgroundSize: "cover", backgroundPosition: "center" }}></div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Ready to save on<br />pharmaceuticals?
          </h2>
          <p className="text-white/75 text-lg max-w-2xl mx-auto mb-8">
            Start with a free account, complete the required credential review, and then decide whether wholesale ordering or optional software fits your business.
          </p>
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-5 py-2 mb-8">
            <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            <span className="text-sm text-white/90 font-medium">Credential review typically takes 1–2 business days</span>
          </div>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/register" className="bg-white text-primary rounded-btn px-8 py-4 font-semibold text-lg hover:bg-gray-100 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5">
              Create a free account
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-6 text-white/70 text-sm">
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              No setup fees
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              No credit card
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              No purchase required
            </span>
          </div>
          <div className="mt-10 flex items-center justify-center gap-6">
            <a href="https://mybadges.us.openbadges.me/api/badgeitem/c44016ac-de61-418c-951f-86c0cddd258b/metadata" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
              <img src="/nabp-badge.png" alt="NABP Accredited Drug Distributor" className="h-14 w-14 object-contain brightness-0 invert" />
            </a>
            <a href="https://www.bbb.org/us/id/boise/profile/pharmaceutical-supplies/premier-pharma-1296-1000131516" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
              <img src="https://seal-alaskaoregonwesternwashington.bbb.org/logo/rbhzbul/bbb-1000131516.png" alt="BBB Accredited Business" className="h-14 object-contain" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
